import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ConsentSettings {
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
  necessary: boolean;
}

const ConsentManagement = () => {
  const [consents, setConsents] = useState<ConsentSettings>({
    analytics: false,
    marketing: false,
    functional: true,
    necessary: true, // Always true, cannot be disabled
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadConsents();
  }, []);

  const loadConsents = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Load from localStorage for now (in a real app, this would be from the database)
      const saved = localStorage.getItem(`consents_${user.id}`);
      if (saved) {
        setConsents(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading consents:', error);
    }
  };

  const handleConsentChange = (type: keyof ConsentSettings, value: boolean) => {
    if (type === 'necessary') return; // Cannot change necessary cookies
    
    setConsents(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const saveConsents = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Save to localStorage for now (in a real app, this would be saved to the database)
      localStorage.setItem(`consents_${user.id}`, JSON.stringify(consents));
      
      toast({
        title: "Consent preferences saved",
        description: "Your privacy preferences have been updated successfully.",
      });
    } catch (error) {
      console.error('Error saving consents:', error);
      toast({
        title: "Error",
        description: "Failed to save consent preferences. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Consent Management</CardTitle>
        <CardDescription>
          Control how we use your data. You can withdraw consent at any time.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between space-x-2">
            <div className="space-y-1">
              <h4 className="text-sm font-medium">Necessary Cookies</h4>
              <p className="text-sm text-gray-600">
                Required for the app to function properly. Cannot be disabled.
              </p>
            </div>
            <Switch
              checked={consents.necessary}
              disabled
              aria-label="Necessary cookies (required)"
            />
          </div>

          <div className="flex items-center justify-between space-x-2">
            <div className="space-y-1">
              <h4 className="text-sm font-medium">Functional Cookies</h4>
              <p className="text-sm text-gray-600">
                Enable enhanced functionality like remembering your preferences.
              </p>
            </div>
            <Switch
              checked={consents.functional}
              onCheckedChange={(value) => handleConsentChange('functional', value)}
              aria-label="Functional cookies"
            />
          </div>

          <div className="flex items-center justify-between space-x-2">
            <div className="space-y-1">
              <h4 className="text-sm font-medium">Analytics Cookies</h4>
              <p className="text-sm text-gray-600">
                Help us understand how you use our app to improve your experience.
              </p>
            </div>
            <Switch
              checked={consents.analytics}
              onCheckedChange={(value) => handleConsentChange('analytics', value)}
              aria-label="Analytics cookies"
            />
          </div>

          <div className="flex items-center justify-between space-x-2">
            <div className="space-y-1">
              <h4 className="text-sm font-medium">Marketing Cookies</h4>
              <p className="text-sm text-gray-600">
                Used to show you relevant advertisements and measure their effectiveness.
              </p>
            </div>
            <Switch
              checked={consents.marketing}
              onCheckedChange={(value) => handleConsentChange('marketing', value)}
              aria-label="Marketing cookies"
            />
          </div>
        </div>

        <Button onClick={saveConsents} disabled={isLoading} className="w-full">
          {isLoading ? "Saving..." : "Save Preferences"}
        </Button>

        <div className="text-xs text-gray-500 space-y-2">
          <p>
            <strong>GDPR Notice:</strong> Under the General Data Protection Regulation, you have the right to withdraw consent at any time.
          </p>
          <p>
            <strong>CCPA Notice:</strong> Under the California Consumer Privacy Act, you have the right to opt-out of the sale of personal information.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ConsentManagement;
